import { useMemo, useState, type ReactNode } from 'react'
import {
  RADII, isLocal, coverageChip, countryWord, wideWhy, placeLine,
  type CataloguePlace, type NearPlace,
} from '@/lib/placeCatalogue'
import {
  usePlaceNear, useDefaultRadius, useHomeParents, useWideScopes, usePlaceSearch, useStateCities, useNearPrefs, useSaveNearPrefs,
} from '@/hooks/usePlaceCatalogue'
import '@/styles/placeDial.css'

/**
 * Add a place is the onboarding dial (Brian's settings-places v3, Oct 30: "within 50 miles, 100 miles, or world,
 * country or state … get rid of that long list"). With no search: the places within the chosen radius of home, larger
 * first, with miles and bearing (and the country, when the border switch is open), then the county, the state, the
 * country (National, naming the topics it serves), other countries and the World. Everything else is a search away.
 * The radius and border choice are saved with the account (near_radius, near_borders) and fall back to onboarding's.
 */
export function PlaceDial({ home, have, mix, onPick, placeholder = 'Search a city, county, state or country' }: {
  /** The account's home place in the catalogue (null while it loads, or for an account without one). */
  home: CataloguePlace | null | undefined
  /** Already on the account: greyed, "Added". */
  have: (p: CataloguePlace) => boolean
  /** The topic mix, so National and World can say which topics they serve. */
  mix: { topic: string; share: number }[]
  onPick: (p: CataloguePlace) => void
  placeholder?: string
}) {
  const [q, setQ] = useState('')
  const { data: prefs } = useNearPrefs()
  const savePrefs = useSaveNearPrefs()
  const ob = (prefs?.onboarding ?? {}) as { radius?: number; borders?: 'home' | 'open' }
  const only = (prefs?.near_borders ?? ob.borders ?? 'home') === 'home'
  const { data: defR } = useDefaultRadius(home, only)
  const radius = prefs?.near_radius ?? ob.radius ?? defR ?? 25
  const local = isLocal(home)
  const { data: near, isFetching } = usePlaceNear(local ? home : null, radius, only, 12)
  const { data: parents } = useHomeParents(home)
  const country = home?.country ?? 'United States'
  const { data: wide } = useWideScopes(country)
  const stateHome = home && (home.kind === 'state' || home.kind === 'province')
  const { data: stateCities } = useStateCities(home?.st, !!stateHome)
  const why = wideWhy(mix)
  const prov = country === 'Canada'
  const across = `Stories from across ${country === 'United States' ? 'the country' : country}`

  const row = (c: CataloguePlace, line?: string) => {
    const h = have(c), [lbl, cls] = coverageChip(c)
    return (
      <button key={`${c.id}`} type="button" className={`opt${h ? ' have' : ''}`} disabled={h} onClick={() => onPick(c)}>
        <span className="t"><span className="n">{c.name}</span><span className="r">{line ?? placeLine(c, home)}</span>
          <span className="cov"><span className={`chip ${cls}`}>{lbl}</span>{c.watch && c.kind !== 'world' && <span className="chip watch">Watch</span>}</span></span>
        {h && <span className="cost">Added</span>}
      </button>
    )
  }
  const grp = (title: string, sub?: string) => <div className="pd-grp" key={`g-${title}`}><b>{title}</b>{sub && <span>{sub}</span>}</div>

  const term = q.trim()
  const { data: found, isFetching: searching } = usePlaceSearch(term, { limit: 40, prefer: country })

  const dial = useMemo(() => {
    const out: ReactNode[] = []
    if (local && home) {
      out.push(grp(`Within ${radius} miles of ${home.name}`, 'Larger places first. Slide the radius out for more.'))
      out.push(
        <div className="pd-radii" key="radii" role="radiogroup" aria-label="Within">
          <small>Within</small>
          {RADII.map((r) => (
            <button key={r} type="button" role="radio" aria-checked={r === radius} className={r === radius ? 'on' : ''}
              onClick={() => savePrefs.mutate({ near_radius: r })}>{r} mi</button>
          ))}
        </div>,
      )
      if (near?.abroad || !only) {
        out.push(
          <div className="pd-radii" key="borders" role="radiogroup" aria-label="Borders">
            <small>Borders</small>
            <button type="button" role="radio" aria-checked={only} className={only ? 'on' : ''} onClick={() => savePrefs.mutate({ near_borders: 'home' })}>Only {countryWord(home.country)}</button>
            <button type="button" role="radio" aria-checked={!only} className={only ? '' : 'on'} onClick={() => savePrefs.mutate({ near_borders: 'open' })}>Across borders</button>
          </div>,
        )
      }
      const bySize = [...(near?.cities ?? [])].sort((a, b) => (a.tier || 4.5) - (b.tier || 4.5) || a.miles - b.miles)
      if (bySize.length) out.push(...bySize.map((c: NearPlace) => row(c, `${c.miles} mi ${c.dir}${c.country !== home.country ? ` · ${c.country}` : ''} · ${c.region}`)))
      else if (near && !isFetching) out.push(<div className="none" key="none">Nothing within {radius} miles. Slide the radius out.</div>)
    } else if (stateHome && home) {
      out.push(grp(`In ${home.name}`, 'Its larger places first.'))
      out.push(...(stateCities ?? []).map((c) => row(c)))
    }
    if (parents?.county) { out.push(grp('Your county')); out.push(row(parents.county)) }
    if (parents?.state && parents.state.id !== home?.id) { out.push(grp(prov ? 'Your province' : 'Your state')); out.push(row(parents.state)) }
    if (wide?.national) {
      out.push(grp('National', why ? `${across}, ${why}` : `${across}. None of your topics are national yet.`))
      out.push(row(wide.national))
    }
    const others = [wide?.continent, ...(wide?.others ?? [])].filter(Boolean) as CataloguePlace[]
    if (others.length) {
      out.push(grp('Other countries', 'News only for now: each country’s top stories in your topics. Any country or region by search.'))
      out.push(...others.map((c) => row(c)))
    }
    if (wide?.world) {
      out.push(grp('World', why ? `Stories from anywhere, ${why}` : 'Stories from anywhere. None of your topics reach that far yet.'))
      out.push(row(wide.world))
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, local, radius, only, near, isFetching, parents, wide, why, stateCities, have])

  // A search ranks the matches (search_places) and tiers them: near your places, around your state, your state,
  // national, other countries, the World, then everywhere else (settings-places HEAD).
  const results = useMemo(() => {
    if (!term || !found) return null
    const stName = parents?.state?.name ?? (stateHome ? home?.name : null)
    const HEAD: [string, string][] = [
      ['Near your places', ''], [stName ? `Around ${stName}` : prov ? 'Around your province' : 'Around your state', ''], [prov ? 'Your province' : 'Your state', ''],
      ['National', why ? `${across}, ${why}` : `${across}. None of your topics are national yet.`],
      ['Other countries', 'News only for now: each country’s top stories in your topics, no local records'],
      ['World', why ? `Stories from anywhere, ${why}` : 'Stories from anywhere. None of your topics reach that far yet.'],
      ['Other places GoGrov covers', ''],
    ]
    const county = home?.kind === 'county' ? `${home.name}, ${home.st}` : home?.kind === 'city' ? home.region : null
    const tierOf = (c: CataloguePlace) => {
      if (c.kind === 'world') return 5
      if (c.kind === 'continent') return 4
      if (c.kind === 'country') return c.country === country ? 3 : 4
      if (c.kind === 'region') return c.country === country ? 2 : 4
      if (county && (c.region === county || (c.kind === 'county' && `${c.name}, ${c.st}` === county))) return 0
      if (home?.st && c.st === home.st) return c.kind === 'state' || c.kind === 'province' ? 2 : 1
      return 6
    }
    const tiered = found.map((c, i) => ({ c, t: tierOf(c), i })).sort((a, b) => a.t - b.t || a.i - b.i)
    const out: ReactNode[] = []
    tiered.forEach(({ c, t }, k) => {
      if (k === 0 || tiered[k - 1].t !== t) out.push(grp(HEAD[t][0], HEAD[t][1] || undefined))
      out.push(row(c))
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, found, home, parents, why, have])

  return (
    <>
      <input className="fx-search" type="search" placeholder={placeholder} aria-label="Search places" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="opts pd-opts">
        {term
          ? (results && results.length ? results
            : term.length < 2 || searching ? <div className="none">Looking…</div>
            : <div className="none">GoGrov doesn’t cover “{term}” yet. Try the nearest city, the county, the state or the country.</div>)
          : dial}
      </div>
    </>
  )
}
